import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  type UseModalProps,
} from "@chakra-ui/react";
import { Trans } from "@lingui/macro";
import { MultiMonthCalendar } from "@src/components/ui-kit";
import { CalendarPicker } from "@src/components/ui-kit/datePicker/CalendarPicker";
import { toDate } from "date-fns";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";

const TAB = {
  Range: 0,
  From: 1,
  To: 2,
};

interface TRange {
  start: Date | undefined;
  end: Date | undefined;
}

interface ModalDatePickerProps extends UseModalProps {
  from?: Date;
  to?: Date;
  onSubmit: (range: TRange) => void;
}

const ModalDatePicker = ({
  from,
  to,
  onSubmit,
  ...modalProps
}: ModalDatePickerProps) => {
  const defaultTab =
    from && to ? TAB.Range : from ? TAB.From : to ? TAB.To : TAB.Range;

  const [dates, setDates] = useState<TRange>({
    start: undefined,
    end: undefined,
  });

  const clearRange = () => setDates({ start: undefined, end: undefined });

  useEffect(() => {
    setDates({ start: from, end: to });
  }, [modalProps.isOpen]);

  const _onSubmit = () => {
    onSubmit(dates);
    modalProps.onClose();
  };

  return (
    <Modal {...modalProps} size="2xl" trapFocus={false}>
      <ModalOverlay />

      <ModalContent>
        <ModalHeader>
          <Trans>Choose date limitation</Trans>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Tabs
            defaultIndex={defaultTab}
            isFitted
            onChange={() => clearRange()}
          >
            <TabList>
              <Tab>
                <Trans>Date range</Trans>
              </Tab>
              <Tab>
                <Trans>Date from</Trans>
              </Tab>
              <Tab>
                <Trans>Date until</Trans>
              </Tab>
            </TabList>
            <TabPanels>
              <TabPanel>
                <MultiMonthCalendar
                  hideCalendarActions
                  value={dates}
                  onSelectDate={(value) => {
                    if (typeof value === "number" || value instanceof Date) {
                      setDates({
                        start: toDate(value),
                        end: undefined,
                      });
                      return;
                    }
                    setDates({
                      start: value.start ? toDate(value.start) : undefined,
                      end: value.end ? toDate(value.end) : undefined,
                    });
                  }}
                />
              </TabPanel>
              <TabPanel>
                <CalendarPicker
                  value={dates}
                  onSelectDate={(value) => {
                    const selectedDate =
                      typeof value === "number" || value instanceof Date
                        ? value
                        : value.start;
                    setDates({
                      start: selectedDate ? toDate(selectedDate) : undefined,
                      end: undefined,
                    });
                  }}
                />
              </TabPanel>
              <TabPanel>
                <CalendarPicker
                  value={dates}
                  onSelectDate={(value) => {
                    const selectedDate =
                      typeof value === "number" || value instanceof Date
                        ? value
                        : value.start;
                    setDates({
                      start: undefined,
                      end: selectedDate ? toDate(selectedDate) : undefined,
                    });
                  }}
                />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>

        <ModalFooter>
          <Button
            colorScheme="grey"
            onClick={modalProps.onClose}
            variant="outline"
          >
            <Trans>Cancel</Trans>
          </Button>
          <Button ml={3} onClick={_onSubmit}>
            <Trans>Save</Trans>
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default observer(ModalDatePicker);
